import type {JSX, ReactNode} from 'react'
import {useEffect} from 'react'
import type {FieldValues, Validate} from 'react-hook-form'
import {useFormContext} from 'react-hook-form'
import {clsx} from 'clsx'
import {logError} from '@/services/logger/client'
import {ErrorMessage} from '../form'

interface InputFieldProps {
	type: string
	label: ReactNode
	name: string
	className?: string
	isOptional?: boolean
	onChange?: (value: string) => void
	maxLength?: number
	requiredErrorMsg?: string
	invalidErrorMsg?: string
	onValidate?:
		| Validate<string, FieldValues>
		| Record<string, Validate<string, FieldValues>>
		| undefined
	defaultValue?: string | number
	disabled?: boolean
	min?: number
	max?: number
}

export const InputField = ({
	defaultValue = '',
	className = '',
	type = 'text',
	label,
	name,
	isOptional = false,
	requiredErrorMsg = '',
	invalidErrorMsg = '',
	onValidate = undefined,
	maxLength = 100,
	onChange,
	disabled = false,
	min = 0,
	max = 100,
}: InputFieldProps): JSX.Element => {
	const requiredMsg = requiredErrorMsg || `${String(label)} is required`
	const invalidMsg =
		invalidErrorMsg || `Please enter a valid ${String(label)}`
	const {register, trigger, formState} = useFormContext()
	useEffect(() => {
		let ignore = false
		async function validation(): Promise<void> {
			if (defaultValue && !ignore) {
				await trigger(name)
			}
		}
		validation().catch((error) => {
			logError(`InputField Validation failed: ${error}`)
		})
		return () => {
			ignore = true
		}
	}, [defaultValue, name, trigger])

	const {
		onChange: libOnChange,
		onBlur: libOnBlur,
		...registerProps
	} = register(name, {
		validate: onValidate,
		required: !isOptional,
		...(type === 'email' && {
			pattern: /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/,
		}),
		...(type === 'tel' && {
			pattern: /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/,
		}),
		...(type === 'number' && {
			min,
			max,
		}),
	})

	return (
		<div className={clsx('relative mb-4', className)}>
			<label className="w-full text-xs text-pet-primary" htmlFor={name}>
				<span>
					{label}
					{!isOptional && <span className="text-red-600">*</span>}
				</span>
				<div className="relative mt-2">
					<input
						{...registerProps}
						className={clsx(
							formState.errors[name] ? 'invalid' : '',
							'text-sky-900 rounded-md !bg-white py-3 text-sm font-normal text-pet-primary'
						)}
						defaultValue={defaultValue}
						disabled={disabled}
						id={name}
						max={max}
						maxLength={maxLength}
						min={min}
						name={name}
						onBlur={(e) => {
							libOnBlur(e).catch((error) => {
								logError(
									`InputField Error in libOnBlur: ${error}`
								)
							})
							onChange?.((e.target.value = e.target.value.trim()))
						}}
						onChange={(e) => {
							libOnChange(e).catch((error) => {
								logError(
									`InputField Error in libOnChange: ${error}`
								)
							})
							onChange?.(e.target.value)
						}}
						type={type}
					/>
					{formState.errors[name] ? (
						<ErrorMessage
							message={
								formState.errors[name]?.type === 'required'
									? requiredMsg
									: invalidMsg
							}
						/>
					) : null}
				</div>
			</label>
		</div>
	)
}
